import { IBoardTypeDeprecated } from "@mctypes/global.types";
import { IAccess } from "@schemas/accessSchema";
import {
  IAddRisikoEvalCategory,
  IBoardBucket,
  IDeleteNoteboardImageNodeInput,
  IIssueBoardApi,
  IIssueViewEnum,
  IMoveIssueBoardToOtherProject,
  INoteboardImageNode,
  IUpdateConsequence,
  IUpdateIsActive,
  IUpdateIssueBoardPredefinedTags,
  IUpdateIssueBoardUseOnlyPredefinedTags,
  IUpdateIssueBoardUsePredefinedTags,
  IUpdateProbability,
} from "@schemas/issueBoardSchema";
import {
  ICountersForAllAttributesWithFormula,
  IDisabledColumnsBody,
  IIssueExcelImportPOSTBody,
} from "@schemas/issueSchema";
import {
  IIssueOtherAttributesUpdateOrderBody,
  IOtherAttributesAddPostBody,
  IOtherAttributesUpdateUpdateBody,
} from "@schemas/otherAttributesSchema";
import { openapi } from "../../../../openapi";
import { apiSlice } from "../../../apiSlice";
import { setShowFailedMessageThunk, setShowSuccessMessageThunk } from "../../../global/globalSlice";
import { AppDispatch, IRootState } from "../../../store";
import { patchNoteboardImageNodesAfterDelete, upsertNoteboardImageNode } from "./boardCachePatches";

export interface IIssueBoardParams {
  projectId: string;
  issueBoardId: string;
}

interface IIssueBoardBucketsParams extends IIssueBoardParams {
  buckets: IBoardBucket[];
}

interface IIssueBoardTableTemplateParams extends IIssueBoardParams {
  tableTemplateId: string;
}

interface IIssueBoardTypesParams extends IIssueBoardParams {
  types: IBoardTypeDeprecated[];
}

interface IIssueBoardBucketsWorkflowParams extends IIssueBoardParams {
  useBucketsAsWorkflow: boolean;
}

interface IUpdateIssueBoardCommonSettings {
  projectId: string;
  issueBoardId: string;
  name: string;
  pns: string;
  status: string;
  showConclusion: boolean;
  defaultView: IIssueViewEnum;
  defaultTemplateId: string;
  issueNameSetting?: string;
  taskNameSetting?: string;
  dashboardSetting?: string;
  category?: string;
  description: string;
  bucketNameSetting?: string;
  showCompletedTodosInKanban?: boolean;
  disableDeleteIssue?: boolean;
}

interface IUpdateIssueBoardAccess {
  projectId: string;
  issueBoardId: string;
  access: IAccess;
}

interface IUpdateIssueBoardAttributeParams extends IIssueBoardParams {
  attribute: string;
  newValue: string | boolean;
}

interface IUpdateIssueBoardDisabledColumnsPut {
  body: IDisabledColumnsBody;
  params: IIssueBoardParams;
}

interface IUpdateIssueBoardNoteboardImageNode extends IIssueBoardParams {
  noteboardImageNode: {
    _id?: string | null; // null if creating a new node
    path: string;
    x: number;
    y: number;
    height: number;
    width: number;
  } | null;
}

export const issueBoardRTK = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSingleIssueBoard: builder.query<IIssueBoardApi, IIssueBoardParams>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/${input.issueBoardId}`,
          method: "GET",
        };
      },
      async onQueryStarted({ issueBoardId, ...createIssueParams }, { dispatch, getState, queryFulfilled }) {
        try {
          const resp = await queryFulfilled;
          const state = getState() as IRootState;
          //  const hasActiveTemplate = state.issueReducerNew.activeTableTemplate != null;

          // if (!hasActiveTemplate) {
          //   dispatch(setIssueAllFiltersInRedux(resp.data.allFilters));
          // }
        } catch (error) {
          console.error(error);
        }
      },
      providesTags: ["SingleIssueBoard"],
    }),
    getIssueBoards: builder.query<IIssueBoardApi[], { projectId: string }>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard`,
          method: "GET",
        };
      },
    }),
    updateIssueBoardSettings: builder.mutation<IIssueBoardApi, IUpdateIssueBoardCommonSettings>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/${input.issueBoardId}`,
          method: "PUT",
          body: {
            name: input.name,
            status: input.status,
            pns: input.pns,
            showConclusion: input.showConclusion,
            defaultView: input.defaultView,
            defaultTemplateId: input.defaultTemplateId,
            issueNameSetting: input.issueNameSetting,
            taskNameSetting: input.taskNameSetting,
            dashboardSetting: input.dashboardSetting,
            category: input.category,
            description: input.description,
            bucketNameSetting: input.bucketNameSetting,
            showCompletedTodosInKanban: input.showCompletedTodosInKanban,
            disableDeleteIssue: input.disableDeleteIssue,
          },
        };
      },
      invalidatesTags: ["SingleIssueBoard"],
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setShowSuccessMessageThunk("Register er oppdatert"));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    updateIssueBoardAttribute: builder.mutation<IIssueBoardApi, IUpdateIssueBoardAttributeParams>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/${input.issueBoardId}/attribute`,
          method: "PUT",
          body: {
            attribute: input.attribute,
            newValue: input.newValue,
          },
        };
      },
      invalidatesTags: ["SingleIssueBoard"],
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setShowSuccessMessageThunk("Attributt er oppdatert"));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    updateIssueBoardBuckets: builder.mutation<IBoardBucket[], IIssueBoardBucketsParams>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/${input.issueBoardId}/buckets`,
          method: "PUT",
          body: {
            buckets: input.buckets,
          },
        };
      },
      invalidatesTags: ["SingleIssueBoard"],
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setShowSuccessMessageThunk("Buckets er oppdatert"));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    updateIssueBoardUseBucketsAsWorkflow: builder.mutation<IIssueBoardApi, IIssueBoardBucketsWorkflowParams>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/${input.issueBoardId}/usebucketsasworkflow`,
          method: "PUT",
          body: {
            useBucketsAsWorkflow: input.useBucketsAsWorkflow,
          },
        };
      },
      invalidatesTags: ["SingleIssueBoard"],
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setShowSuccessMessageThunk("Buckets er oppdatert"));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    updateIssueBoardTypes: builder.mutation<IBoardTypeDeprecated[], IIssueBoardTypesParams>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/${input.issueBoardId}/types`,
          method: "PUT",
          body: {
            types: input.types,
          },
        };
      },
      invalidatesTags: ["SingleIssueBoard"],
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setShowSuccessMessageThunk("Typer er oppdatert"));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    deleteIssueBoardTableTemplate: builder.mutation<void, IIssueBoardTableTemplateParams>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/${input.issueBoardId}/tableTemplate/${input.tableTemplateId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["SingleIssueBoard"],
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setShowSuccessMessageThunk("Register mal er slettet"));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    // updateIssueBoardTableTemplate: builder.mutation<
    //   ITableTemplate,
    //   { params: IIssueBoardTableTemplateParams; body: IUpdateTableTemplateBody }
    // >({
    //   query(input) {
    //     return {
    //       url: `/project/${input.params.projectId}/issueboard/${input.params.issueBoardId}/tableTemplate/${input.params.tableTemplateId}`,
    //       method: "PUT",
    //       body: input.body,
    //     };
    //   },
    //   invalidatesTags: ["SingleIssueBoard"],
    //   async onQueryStarted({}, { dispatch, queryFulfilled }) {
    //     try {
    //       const resp = await queryFulfilled;
    //       dispatch(loadIssueTableTemplateThunk(resp.data, resp.data.issueBoard));
    //       dispatch(setShowSuccessMessageThunk("Register mal er oppdatert"));
    //     } catch (error) {
    //       console.error(error);
    //     }
    //   },
    // }),
    // createIssueBoardTableTemplates: builder.mutation<
    //   ITableTemplate,
    //   { projectId: string; issueBoardId: string; body: IUpdateTableTemplateBody }
    // >({
    //   query(input) {
    //     return {
    //       url: `/project/${input.projectId}/issueboard/${input.issueBoardId}/tableTemplate`,
    //       method: "POST",
    //       body: input.body,
    //     };
    //   },
    //   invalidatesTags: ["SingleIssueBoard"],
    //   async onQueryStarted({}, { dispatch, queryFulfilled }) {
    //     try {
    //       const resp = await queryFulfilled;
    //       dispatch(loadIssueTableTemplateThunk(resp.data, resp.data.issueBoard));
    //       dispatch(setShowSuccessMessageThunk("Register mal er opprettet"));
    //     } catch (error) {
    //       console.error(error);
    //     }
    //   },
    // }),
    getIssueBoardTemplates: builder.query<IIssueBoardApi[], { projectId: string }>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/template/all`,
          method: "GET",
        };
      },
    }),
    createIssueBoard: builder.mutation<
      IIssueBoardApi,
      { projectId: string; name: string; boardIdToCopySettingsFrom?: string; copyIssuesAndTasks?: boolean }
    >({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard`,
          method: "POST",
          body: {
            name: input.name,
            boardIdToCopySettingsFrom: input.boardIdToCopySettingsFrom,
            copyIssuesAndTasks: input.copyIssuesAndTasks,
          },
        };
      },
      invalidatesTags: ["SingleIssueBoard"],
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setShowSuccessMessageThunk("Register er opprettet"));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    createIssueBoardFromExcel: builder.mutation<void, { projectId: string; body: IIssueExcelImportPOSTBody }>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/import-excel`,
          method: "POST",
          body: input.body,
        };
      },
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setShowSuccessMessageThunk("Board er opprettet"));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    updateIssueBoardAccess: builder.mutation<IIssueBoardApi, IUpdateIssueBoardAccess>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/${input.issueBoardId}/access`,
          method: "PUT",
          body: {
            access: input.access,
          },
        };
      },
      invalidatesTags: ["SingleIssueBoard"],
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setShowSuccessMessageThunk("Tilgang er oppdatert"));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    updateIssueBoardPredefinedTags: builder.mutation<IIssueBoardApi, IUpdateIssueBoardPredefinedTags>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/predefinedtags.update`,
          method: "POST",
          body: input,
        };
      },
      invalidatesTags: ["SingleIssueBoard"],
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setShowSuccessMessageThunk("Forhåndsdefinerte tags er oppdatert"));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    updateIssueBoardUsePredefinedTags: builder.mutation<IIssueBoardApi, IUpdateIssueBoardUsePredefinedTags>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/usepredefinedtags.update`,
          method: "POST",
          body: input,
        };
      },
      invalidatesTags: ["SingleIssueBoard"],
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setShowSuccessMessageThunk("Bruk av forhåndsdefinerte tags er oppdatert"));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    updateIssueBoardUseOnlyPredefinedTags: builder.mutation<IIssueBoardApi, IUpdateIssueBoardUseOnlyPredefinedTags>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/useonlypredefinedtags.update`,
          method: "POST",
          body: input,
        };
      },
      invalidatesTags: ["SingleIssueBoard"],
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setShowSuccessMessageThunk("Låsing til forhåndsdefinerte tags er oppdatert"));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    updateIssueBoardRisikoIsActive: builder.mutation<IIssueBoardApi, IUpdateIsActive>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/risiko.isactive`,
          method: "POST",
          body: input,
        };
      },
      invalidatesTags: ["SingleIssueBoard"],
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setShowSuccessMessageThunk("Risiko er aktivet"));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    updateRisikoProbabilitiesIsActiveAfterTiltak: builder.mutation<IIssueBoardApi, IUpdateIsActive>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/risiko.probability.isactiveaftertiltak`,
          method: "POST",
          body: input,
        };
      },
      invalidatesTags: ["SingleIssueBoard"],
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setShowSuccessMessageThunk("Sannsynligheter er oppdatert"));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    updateRisikoProbabilities: builder.mutation<IIssueBoardApi, IUpdateProbability>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/risiko.probability.update`,
          method: "POST",
          body: input,
        };
      },
      invalidatesTags: ["SingleIssueBoard"],
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setShowSuccessMessageThunk("Sannsynligheter er oppdatert"));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    updateIssueBoardDisabledColumns: builder.mutation<IIssueBoardApi, IUpdateIssueBoardDisabledColumnsPut>({
      query(input) {
        return {
          url: `/project/${input.params.projectId}/issueboard/${input.params.issueBoardId}/disabledColumns`,
          method: "PUT",
          body: input.body,
        };
      },
      invalidatesTags: ["SingleIssueBoard"],
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setShowSuccessMessageThunk("Kolonner er oppdatert"));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    addIssueBoardOtherAttribute: builder.mutation<void, { projectId: string; body: IOtherAttributesAddPostBody }>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/otherAttributesConfig.add`,
          method: "POST",
          body: input.body,
        };
      },
      invalidatesTags: ["SingleIssueBoard"],
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setShowSuccessMessageThunk("Egendefinert attributt er lagt til"));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    updateIssueBoardOtherAttribute: builder.mutation<
      void,
      { projectId: string; body: IOtherAttributesUpdateUpdateBody }
    >({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/otherAttributesConfig.update`,
          method: "POST",
          body: input.body,
        };
      },
      invalidatesTags: ["SingleIssueBoard"],
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setShowSuccessMessageThunk("Egendefinert attributt er oppdatert"));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    updateIssueBoardOtherAttributeOrder: builder.mutation<
      void,
      { projectId: string; body: IIssueOtherAttributesUpdateOrderBody }
    >({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/otherAttributesConfig.updateorder`,
          method: "POST",
          body: input.body,
        };
      },
      invalidatesTags: ["SingleIssueBoard"],
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setShowSuccessMessageThunk("Rekkefølgen på egendefinerte attributter er oppdatert"));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    addRisikoEvaluationCategory: builder.mutation<IIssueBoardApi, IAddRisikoEvalCategory>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/risiko.evaluationcategory.add`,
          method: "POST",
          body: input,
        };
      },
      invalidatesTags: ["SingleIssueBoard"],
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setShowSuccessMessageThunk("Evalueringskategori lagt til"));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    updateRisikoEvaluationCategory: builder.mutation<IIssueBoardApi, IUpdateConsequence>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/risiko.evaluationcategory.update`,
          method: "POST",
          body: input,
        };
      },
      invalidatesTags: ["SingleIssueBoard"],
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setShowSuccessMessageThunk("Evaluering oppdatert"));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    moveIssueBoardToOtherProject: builder.mutation<
      IIssueBoardApi,
      IMoveIssueBoardToOtherProject & { projectId: string }
    >({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/move.issueboard.to.project`,
          method: "POST",
          body: input,
        };
      },
      invalidatesTags: ["IssueBoard"],
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setShowSuccessMessageThunk("Register flyttet"));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    getSumsByFormula: builder.query<ICountersForAllAttributesWithFormula, { projectId: string; issueBoardId: string }>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/otherAttributesConfig.getSumsByFormula`,
          method: "POST",
          body: {
            issueBoardId: input.issueBoardId,
          },
        };
      },
      providesTags: ["IssueFormulas"],
    }),
    addNoteboardImageNode: builder.mutation<INoteboardImageNode, IUpdateIssueBoardNoteboardImageNode>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/noteboardImageNode.add`,
          method: "POST",
          body: input,
        };
      },
      async onQueryStarted({ projectId, issueBoardId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // data = serverns node (med “_id”, ev. “visible”, mm)
          upsertNoteboardImageNode(dispatch as any, { projectId, issueBoardId }, data);
        } catch (err) {
          console.warn("updateNoteboardImageNode failed:", err);
          // inget att rulla tillbaka eftersom vi inte gjorde någon optimistic patch
        }
      },
    }),
    updateNoteboardImageNode: builder.mutation<INoteboardImageNode, IUpdateIssueBoardNoteboardImageNode>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/noteboardImageNode.update`,
          method: "POST",
          body: input,
        };
      },
      // PESSIMISTISK: vänta på svar, patcha cache – ingen refetch behövs
      async onQueryStarted({ projectId, issueBoardId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          upsertNoteboardImageNode(dispatch as any, { projectId, issueBoardId }, data);
        } catch (err) {
          console.warn("updateNoteboardImageNode failed:", err);
        }
      },
    }),
    deleteNoteboardImageNode: builder.mutation<IIssueBoardApi, IDeleteNoteboardImageNodeInput>({
      query(input) {
        return {
          url: `/project/${input.projectId}/issueboard/noteboardImageNode.delete`,
          method: "POST",
          body: input,
        };
      },
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data: serverBoard } = await queryFulfilled;
          patchNoteboardImageNodesAfterDelete(dispatch as AppDispatch, args);
          dispatch(setShowSuccessMessageThunk("Bild slettet"));
        } catch (err) {
          dispatch(setShowFailedMessageThunk("Kunne ikke lagre endring"));
          const undo = patchNoteboardImageNodesAfterDelete(dispatch as AppDispatch, args);
          undo?.();
          console.warn("deleteNoteboardImageNode failed, rollback applied:", err);
        }
      },
    }),
  }),
});

export const {
  useGetSingleIssueBoardQuery,
  useGetIssueBoardsQuery,
  useUpdateIssueBoardSettingsMutation,
  useUpdateIssueBoardAttributeMutation,
  useUpdateIssueBoardBucketsMutation,
  useUpdateIssueBoardUseBucketsAsWorkflowMutation,
  useUpdateIssueBoardTypesMutation,
  useDeleteIssueBoardTableTemplateMutation,
  useGetIssueBoardTemplatesQuery,
  useCreateIssueBoardMutation,
  useCreateIssueBoardFromExcelMutation,
  useUpdateIssueBoardAccessMutation,
  useUpdateIssueBoardDisabledColumnsMutation,
  useAddIssueBoardOtherAttributeMutation,
  useUpdateIssueBoardOtherAttributeMutation,
  useUpdateIssueBoardOtherAttributeOrderMutation,
  useUpdateIssueBoardPredefinedTagsMutation,
  useUpdateIssueBoardUseOnlyPredefinedTagsMutation,
  useUpdateIssueBoardUsePredefinedTagsMutation,
  useUpdateIssueBoardRisikoIsActiveMutation,
  useUpdateRisikoProbabilitiesIsActiveAfterTiltakMutation,
  useUpdateRisikoProbabilitiesMutation,
  useAddRisikoEvaluationCategoryMutation,
  useUpdateRisikoEvaluationCategoryMutation,
  useMoveIssueBoardToOtherProjectMutation,
  useGetSumsByFormulaQuery,
  useAddNoteboardImageNodeMutation,
  useUpdateNoteboardImageNodeMutation,
  useDeleteNoteboardImageNodeMutation,
} = issueBoardRTK;

const enhancedApi = openapi.enhanceEndpoints({
  endpoints: {
    putProjectByProjectIdGanttVacations: {
      async onQueryStarted({ projectId, body }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled; // wait for backend success
          dispatch(issueBoardRTK.util.invalidateTags(["SingleIssueBoard"]));
          dispatch(setShowSuccessMessageThunk("Ferie oppdatert"));
        } catch {
          dispatch(setShowFailedMessageThunk("Kunne ikke oppdatere ferie"));
        }
      },
    },
    postProjectByProjectIdIssueboardPredefinedtagsUpdate: {
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(issueBoardRTK.util.invalidateTags(["SingleIssueBoard"]));
          dispatch(setShowSuccessMessageThunk("Forhåndsdefinerte tags er oppdatert"));
        } catch {
          dispatch(setShowFailedMessageThunk("Kunne ikke oppdatere forhåndsdefinerte tags"));
        }
      },
    },
    postProjectByProjectIdIssueboardUsepredefinedtagsUpdate: {
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(issueBoardRTK.util.invalidateTags(["SingleIssueBoard"]));
          dispatch(setShowSuccessMessageThunk("Bruk av forhåndsdefinerte tags er oppdatert"));
        } catch {
          dispatch(setShowFailedMessageThunk("Kunne ikke oppdatere bruk av forhåndsdefinerte tags"));
        }
      },
    },
  },
});
